import { NgClass, NgTemplateOutlet } from '@angular/common';
import { TranslateDirective } from '@ngx-translate/core';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  InjectionToken,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges
} from '@angular/core';
import { HrefPipe } from '../../../../pipes/href.pipe';
import { IconComponent } from '../../../../components/icon/icon.component';
import { MarketType } from '../../../../domain/public-api';

export const ANALYST_RATING_CONFIGURATION = new InjectionToken<AnalystRatingConfiguration>('analystRatingConfiguration');

export enum AnalystRatings {
  BUY = 'BUY',
  STRONG_BUY = 'STRONG_BUY',
  SELL = 'SELL',
  STRONG_SELL = 'STRONG_SELL',
  HOLD = 'HOLD',
  UNDERPERFORM = 'UNDERPERFORM'
}

export interface RatingsConsensus {
  overallConsensus: AnalystRatings;
  numberOfAnalysts: number;
  ratingBuy: number;
  ratingHold: number;
  ratingSell: number;
}

export interface AnalystRatingConfiguration {
  newAnalystRatings: boolean;
  analystRatingLinkId?: string;
}

/**
 *
 * Graphically depicts the analyst consensus for a financial security.
 * An example usage of the directive:
 *
 * <td-wb-analyst-rating [consensus]="quote.ratingConsensus">
 * </td-wb-analyst-rating>
 *
 *  @param {string} consensus The analyst consensus, expected to be an element in the
 *  set { 'SELL', 'UNDERPERFORM', 'HOLD', 'BUY', 'STRONG_BUY', 'STRONG_SELL'}.
 */

@Component({
  selector: 'td-wb-analyst-rating',
  templateUrl: 'analyst-rating.component.html',
  styleUrls: ['analyst-rating.component.scss'],
  imports: [
    NgTemplateOutlet,
    TranslateDirective,
    NgClass,
    HrefPipe,
    IconComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class AnalystRatingComponent implements OnInit, OnChanges {
  private static readonly ANALYST_RATING_LINK: string = 'link_markets_research_analysts';

  @Input() consensus: RatingsConsensus | AnalystRatings;
  @Input() symbol: string;
  @Input() market: MarketType;

  overallConsensus: AnalystRatings;
  newAnalystRatings: boolean;
  linkId: string = AnalystRatingComponent.ANALYST_RATING_LINK;
  // TODO Deprecate
  // eslint-disable-next-line @typescript-eslint/naming-convention
  readonly AnalystRatings: typeof AnalystRatings = AnalystRatings;

  constructor(@Inject(ANALYST_RATING_CONFIGURATION) private analystRatingsConfiguration: AnalystRatingConfiguration) {
  }

  ngOnInit(): void {
    this.newAnalystRatings = this.analystRatingsConfiguration !== undefined && this.analystRatingsConfiguration.newAnalystRatings;
    if (this.analystRatingsConfiguration.analystRatingLinkId !== undefined) {
      this.linkId = this.analystRatingsConfiguration.analystRatingLinkId;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Check what type consensus is. Old type is AnalystRatings, new object is RatingsConsensus.
    // Eventually it will always be RatingsConsensus
    if (changes.consensus !== undefined) {
      const consensus = changes.consensus.currentValue;
      this.overallConsensus = consensus !== undefined && typeof consensus === 'object' ?
        consensus.overallConsensus : consensus as AnalystRatings;
    }
  }

  isRatingsConsensus(value: RatingsConsensus | AnalystRatings): value is RatingsConsensus {
    return value !== undefined && this.overallConsensus !== undefined;
  }

  showNewAnalystRatings(): boolean {
    return this.newAnalystRatings;
  }

  showAnalystRatings(): boolean {
    return this.overallConsensus !== undefined;
  }

  isRating(rating: AnalystRatings): boolean {
    let adjustedRating: AnalystRatings;
    if (this.overallConsensus === AnalystRatings.SELL || this.overallConsensus === AnalystRatings.STRONG_SELL) {
      adjustedRating =  AnalystRatings.SELL;
    } else if (this.overallConsensus === AnalystRatings.BUY || this.overallConsensus === AnalystRatings.STRONG_BUY) {
      adjustedRating = AnalystRatings.BUY;
    } else {
      adjustedRating = this.overallConsensus;
    }
    return adjustedRating === rating;
  }

  getLinkParams(): string {
    return `/${this.market}/${this.symbol}`;
  }
}
